import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from "@mui/material";
import { toast } from "react-toastify";
import axiosInstance from "../../../../api/axiosConfig";

function FundWalletModal({ open, onClose, onPaymentSuccess }) {
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("bank_transfer"); // 'card' or 'bank_transfer'
  const [loading, setLoading] = useState(false);
  const [transferDetails, setTransferDetails] = useState(null);

  const handleReset = () => {
    setAmount("");
    setPaymentMethod("bank_transfer");
    setTransferDetails(null);
    setLoading(false);
  };

  const handleCloseModal = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || Number(amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    try {
      setLoading(true);
      const response = await axiosInstance.post(
        "/marketplace/getequity/fund-wallet/",
        {
          amount: Number(amount),
          method: paymentMethod,
        },
      );

      const data = response.data?.data;

      if (paymentMethod === "card" && data?.paymentLink) {
        // Direct redirect for card payment in the same tab
        window.location.href = data.paymentLink;
      } else if (paymentMethod === "bank_transfer" && data?.virtualAccount) {
        // Show virtual bank transfer details in modal
        setTransferDetails(data);
      } else {
        toast.error("Unexpected response from server.");
      }
    } catch (error) {
      console.error("Funding error:", error);
      toast.error(
        error.response?.data?.message || "Failed to initiate funding.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmTransferPayment = async () => {
    if (onPaymentSuccess) {
      await onPaymentSuccess();
    }
    handleCloseModal();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: "#ffffff",
          color: "#000000",
          borderRadius: 1,
          border: "1px solid #000000",
        },
      }}
    >
      <DialogTitle
        sx={{ fontWeight: "bold", borderBottom: "1px solid #e0e0e0" }}
      >
        {transferDetails ? "Complete Bank Transfer" : "Fund Wallet"}
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {!transferDetails ? (
          /* STEP 1: INPUT FORM */
          <Box component="form" onSubmit={handleSubmit} sx={{ pt: 1 }}>
            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#333333" }}
            >
              AMOUNT (NGN)
            </Typography>
            <TextField
              fullWidth
              size="small"
              type="number"
              placeholder="e.g. 100000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              sx={{
                mt: 1,
                mb: 3,
                "& .MuiOutlinedInput-root": {
                  borderRadius: 0,
                  "&.Mui-focused fieldset": { borderColor: "#000000" },
                },
              }}
            />

            <Typography
              variant="caption"
              sx={{ fontWeight: 600, color: "#333333" }}
            >
              PAYMENT METHOD
            </Typography>
            <RadioGroup
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              sx={{ mt: 1 }}
            >
              <FormControlLabel
                value="card"
                control={
                  <Radio
                    sx={{
                      color: "#000000",
                      "&.Mui-checked": { color: "#000000" },
                    }}
                  />
                }
                label={<Typography variant="body2">Pay with Card</Typography>}
              />
              <FormControlLabel
                value="bank_transfer"
                control={
                  <Radio
                    sx={{
                      color: "#000000",
                      "&.Mui-checked": { color: "#000000" },
                    }}
                  />
                }
                label={
                  <Typography variant="body2">Pay with Transfer</Typography>
                }
              />
            </RadioGroup>
          </Box>
        ) : (
          /* STEP 2: TRANSFER DETAILS DISPLAY */
          <Box sx={{ pt: 1 }}>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Transfer exact amount to the virtual account details below to
              complete your wallet funding.
            </Typography>

            <Box
              sx={{
                border: "1px solid #000000",
                p: 2,
                bgcolor: "#fcfcfc",
                mb: 2,
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  BANK NAME
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {transferDetails.virtualAccount?.bankName}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  ACCOUNT NUMBER
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight="bold"
                  sx={{ letterSpacing: 1 }}
                >
                  {transferDetails.virtualAccount?.accountNumber}
                </Typography>
              </Box>
              <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
              >
                <Typography variant="caption" color="text.secondary">
                  AMOUNT TO PAY
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {transferDetails.currency}{" "}
                  {Number(transferDetails.totalCharge).toLocaleString()}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="caption" color="text.secondary">
                  EXPIRES AT
                </Typography>
                <Typography variant="caption" fontWeight="bold">
                  {transferDetails.virtualAccount?.expiresAt}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}
            >
              <Typography variant="caption">Base Amount:</Typography>
              <Typography variant="caption">
                {transferDetails.currency}{" "}
                {Number(transferDetails.amount).toLocaleString()}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography variant="caption">Fees:</Typography>
              <Typography variant="caption">
                {transferDetails.currency}{" "}
                {Number(transferDetails.fees?.total || 0).toLocaleString()}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button
          onClick={handleCloseModal}
          disabled={loading}
          sx={{
            color: "#000000",
            borderRadius: 0,
            textTransform: "none",
          }}
        >
          Cancel
        </Button>

        {!transferDetails ? (
          <Button
            onClick={handleSubmit}
            disabled={loading}
            variant="contained"
            sx={{
              bgcolor: "#000000",
              color: "#ffffff",
              borderRadius: 0,
              textTransform: "none",
              "&:hover": { bgcolor: "#333333" },
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : (
              "Proceed"
            )}
          </Button>
        ) : (
          <Button
            onClick={handleConfirmTransferPayment}
            variant="contained"
            sx={{
              bgcolor: "#000000",
              color: "#ffffff",
              borderRadius: 0,
              textTransform: "none",
              "&:hover": { bgcolor: "#333333" },
            }}
          >
            Confirm Payment
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default FundWalletModal;
